import { ServiceItem } from '../types';
import { documentModuleByTitle } from './billingSelection';
import { hatBestandteile } from './bestandteile/bestandteilLogic';
import { isNepService } from './nepPosition';
import { isMonthlyUnit } from './serviceUnits';

/**
 * Welche LV-Positionen der Zusatzleistungs-Dialog beim Versand anbietet
 * („heute durchgefuehrt? Menge eingeben").
 *
 * Bisher: alles mit Zuordnung 'additional' oder 'none'. Das traf auch
 * Monatspositionen ohne Zuordnung — Projekt 26041002 trug neben der echten
 * Monatspauschale (assignment 'flatrate') noch das Alt-Honorar „2 x 320 EUR /
 * Monat" mit Zuordnung 'none', und der Dialog bot es bei jedem Versand als
 * Zusatzleistung an. Eine Monatsgebuehr wird nicht „heute durchgefuehrt";
 * sie wird ueber den Monatsvorschlag gebucht (flatRateLogic) — ein Klick im
 * Zusatzleistungs-Dialog schriebe stattdessen einen zusaetzlichen Monat auf
 * die Position.
 *
 * Ausgeschlossen sind deshalb alle Positionen mit Monatseinheit und alle mit
 * Pauschal-Deckung, unabhaengig von ihrer Zuordnung. Nur nach Menge und
 * Zuordnung sortieren — Titel und Nummer gehoeren dem Kunden und steuern
 * hier nichts.
 *
 * NEP-Positionen (Eventualpositionen, nicht beauftragt) bleiben ebenfalls
 * draussen: eine Buchung darauf braucht eine Begruendung, und die kann nur
 * der Buchungsdialog im Projekt-Hub abfragen — der Versanddialog kennt je
 * Position nur eine Menge. Ohne den Ausschluss buchte der Versand eine
 * nicht beauftragte Leistung mit einem Fingertipp und ohne jedes Warum.
 *
 * Ebenfalls draussen: Positionen OHNE Zuordnung, deren Titel ein
 * Dokumentmodul trifft (Vorankuendigung, Unterlage, SiGe-Plan — siehe
 * documentModuleByTitle). Projekt 5634 trug aus der Alt-Rechnung „Honorar
 * Vorankündigung" und „Honorar Erstellung SiGe-Plan" mit Zuordnung 'none';
 * der Dialog bot beide bei jedem Versand an, und bei den Berichten #52/#53
 * wurden sie ein zweites Mal gebucht, obwohl der Vertrag (1 pauschal) laengst
 * erbracht und abgerechnet war. Gebucht werden sie in ihrem Modul. Wer
 * „Zusatzleistung" ausdruecklich als Zuordnung setzt, behaelt die Position
 * hier — die Zuordnung schlaegt den Titel.
 *
 * Und draussen: PAKETPOSITIONEN (Positionen mit Bestandteilen, Plan
 * Kernregel 7). Sie tragen oft `none` oder `planning_general` und stuenden
 * sonst mit „heute durchgefuehrt?" im Versanddialog — ein Klick buchte das
 * ganze Paket, waehrend Bestandteile offen sind. Gebucht wird ein Paket nur,
 * wenn sein letzter Bestandteil erledigt ist (Abschlussschritt bzw. Dialog
 * der Bestandteile, sonst Nachtlauf). Die zweite Sperre steht in
 * `reportBooking` (Zusatzleistung auf ein Paket wird nicht gebucht).
 */
export const isAdditionalServiceCandidate = (s: Pick<ServiceItem, 'assignment' | 'title' | 'unit' | 'flatRate' | 'is_optional' | 'bestandteile'>): boolean =>
    (s.assignment === 'additional' || s.assignment === 'none')
    && documentModuleByTitle(s) === null
    && !isMonthlyUnit(s.unit)
    && !s.flatRate
    && !isNepService(s)
    && !hatBestandteile(s);

export const additionalServiceCandidates = (services: ServiceItem[] | undefined | null): ServiceItem[] =>
    (services || []).filter(isAdditionalServiceCandidate);

/** Eine eingetragene Zusatzleistung, die ueber den Vertragsrest hinausgeht. */
export interface OverContractAddon {
    serviceId: string;
    lvPosition: string;
    title: string;
    unit: string;
    quantity: number;
    /** Vertrag minus erbracht, nie negativ — derselbe Wert wie „Rest" im Dialog. */
    remaining: number;
}

/**
 * Welche eingetragenen Mengen den Vertragsrest ueberschreiten — Grundlage der
 * Rueckfrage im Zusatzleistungs-Dialog. Bisher zeigte der Dialog „Rest: 0"
 * nur in Rot und buchte trotzdem (5634: eine Pauschale ein zweites und
 * drittes Mal). Keine Sperre: eine echte Mehrleistung bleibt buchbar, sie
 * muss nur bestaetigt werden.
 *
 * Reihenfolge wie die Dialogliste. Toleranz 0,0005 wie an den uebrigen
 * Mengenvergleichen, damit 0,1 + 0,2 nicht als Ueberschreitung zaehlt.
 */
export const overContractAddons = (
    services: ServiceItem[],
    quantities: Record<string, number>,
): OverContractAddon[] =>
    services.flatMap(s => {
        const quantity = quantities[s.id];
        if (!(quantity > 0)) return [];
        const remaining = Math.max(0, (Number(s.quantityContracted) || 0) - (Number(s.quantityUsed) || 0));
        if (quantity <= remaining + 0.0005) return [];
        return [{
            serviceId: s.id,
            lvPosition: s.lvPosition || '',
            title: s.title || '',
            unit: s.unit || '',
            quantity,
            remaining,
        }];
    });
